import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Calculator, FloppyDisk, MagicWand, ShieldCheck, Warning, XCircle } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, Cell } from "recharts";

const money = (n) => `$${Number(n || 0).toLocaleString("es-MX", { maximumFractionDigits: 0 })}`;

const GROUPS = [
  { title: "Inversión", tone: "sky", fields: [
    ["capex", "Inversión inicial (CAPEX)", "$"], ["working_capital", "Capital de trabajo", "$"],
    ["residual_value", "Valor residual equipos", "$"], ["horizon_months", "Horizonte (meses)", "m"] ] },
  { title: "Tasas", tone: "violet", fields: [
    ["discount_rate_annual", "Tasa de descuento anual", "%"], ["tax_rate", "ISR", "%"] ] },
  { title: "Ingresos mensuales", tone: "emerald", fields: [
    ["price_per_ml", "Precio cobrado por ml", "$/ml"], ["ml_per_month", "Metros planeados / mes", "ml"],
    ["other_income_month", "Otros ingresos / mes", "$"] ] },
  { title: "Costos mensuales", tone: "rose", fields: [
    ["variable_cost_per_ml", "Costo variable por ml (destajo, explosivos, aceros)", "$/ml"],
    ["fixed_cost_month", "Costos fijos / mes (nómina, campamento, admin)", "$"],
    ["maintenance_month", "Mantenimiento equipos / mes", "$"] ] },
];

const VERDICT = {
  rentable: { Icon: ShieldCheck, cls: "bg-emerald-600 text-white", bar: "#059669" },
  marginal: { Icon: Warning, cls: "bg-amber-500 text-slate-900", bar: "#d97706" },
  no_rentable: { Icon: XCircle, cls: "bg-rose-600 text-white", bar: "#e11d48" },
};

function Kpi({ label, value, sub, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{label}</p>
      <p className="font-heading font-black text-xl text-slate-900 leading-tight">{value}</p>
      {sub && <p className="text-[10px] text-slate-500 mt-0.5">{sub}</p>}
    </div>
  );
}

export default function ProfitabilityPanel({ canWrite }) {
  const [p, setP] = useState(null);
  const [ev, setEv] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/finance/profitability/params").then((r) => setP(r.data)).catch((e) => toast.error(apiError(e))); }, []);

  useEffect(() => {
    if (!p) return;
    const t = setTimeout(() => api.post("/finance/profitability/evaluate", p).then((r) => setEv(r.data)).catch(() => {}), 300);
    return () => clearTimeout(t);
  }, [p]);

  const upd = (k, v) => setP((s) => ({ ...s, [k]: k === "horizon_months" ? parseInt(v, 10) || 1 : parseFloat(v) || 0 }));

  const save = async () => {
    setBusy(true);
    try { await api.put("/finance/profitability/params", p); toast.success("Supuestos de rentabilidad guardados"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const prefill = useCallback(async () => {
    try {
      const { data } = await api.get("/finance/profitability/actuals?months=6");
      if (!data.ml_month) { toast.info("Sin datos reales suficientes (ml) en los últimos 6 meses"); return; }
      setP((s) => ({ ...s, price_per_ml: data.price_per_ml, ml_per_month: data.ml_month,
        variable_cost_per_ml: Math.round(data.cost_per_ml * 0.6 * 100) / 100,
        fixed_cost_month: Math.round(data.gasto_month * 0.4) }));
      toast.success(`Supuestos desde reales: ${data.ml_month} ml/mes · ${money(data.ingreso_month)}/mes`);
    } catch (e) { toast.error(apiError(e)); }
  }, []);

  if (!p) return null;
  const v = ev ? VERDICT[ev.verdict] : null;

  return (
    <div className="bg-white border border-indigo-200 rounded-lg p-4 space-y-4" data-testid="fin-profitability">
      <div className="flex flex-wrap items-center gap-2">
        <Calculator size={16} weight="fill" className="text-indigo-700" />
        <h3 className="font-heading font-bold text-indigo-700 uppercase text-xs tracking-widest">Evaluación de rentabilidad del proyecto</h3>
        <span className="text-[10px] text-slate-500">— VAN · TIR · payback · punto de equilibrio</span>
        <div className="flex-1" />
        <Button size="sm" variant="outline" onClick={prefill} className="gap-1 text-xs" data-testid="prof-prefill">
          <MagicWand size={13} /> Tomar de reales (6 meses)
        </Button>
        {canWrite && (
          <Button size="sm" onClick={save} disabled={busy} className="bg-indigo-600 hover:bg-indigo-500 text-white gap-1 text-xs" data-testid="prof-save">
            <FloppyDisk size={13} weight="bold" /> {busy ? "Guardando…" : "Guardar supuestos"}
          </Button>
        )}
      </div>

      {ev && v && (
        <div className={`rounded-lg px-4 py-3 flex flex-wrap items-center gap-3 ${v.cls}`} data-testid="prof-verdict">
          <v.Icon size={28} weight="fill" />
          <div>
            <p className="font-heading font-black text-lg leading-tight" data-testid="prof-verdict-label">{ev.verdict_label}</p>
            <p className="text-[11px] opacity-90">{ev.reasons.join(" · ")}</p>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-4 gap-3">
        {GROUPS.map((g) => (
          <div key={g.title} className={`border border-${g.tone}-200 bg-${g.tone}-50/40 rounded-lg p-3 space-y-2`}>
            <p className={`text-[10px] uppercase tracking-widest font-bold text-${g.tone}-700`}>{g.title}</p>
            {g.fields.map(([k, l, u]) => (
              <div key={k}>
                <Label className="text-[10px] text-slate-600">{l} <span className="text-slate-400">({u})</span></Label>
                <Input type="number" step="any" value={p[k] ?? 0} onChange={(e) => upd(k, e.target.value)}
                  disabled={!canWrite} className="h-8 text-right font-mono text-xs bg-white" data-testid={`prof-input-${k}`} />
              </div>
            ))}
          </div>
        ))}
      </div>

      {ev && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
            <Kpi label="VAN" value={money(ev.npv)} sub={`tasa ${p.discount_rate_annual}% anual`} testid="prof-npv" />
            <Kpi label="TIR anual" value={ev.irr_annual_pct == null ? "—" : `${ev.irr_annual_pct}%`} sub={`vs ${p.discount_rate_annual}% exigido`} testid="prof-irr" />
            <Kpi label="Payback" value={ev.payback_months == null ? "No recupera" : `${ev.payback_months} m`} sub={`descontado: ${ev.discounted_payback_months ?? "—"} m`} testid="prof-payback" />
            <Kpi label="Punto equilibrio" value={ev.break_even_ml_month == null ? "—" : `${ev.break_even_ml_month} ml/mes`} sub={ev.break_even_revenue_month != null ? money(ev.break_even_revenue_month) + "/mes" : ""} testid="prof-breakeven" />
            <Kpi label="Margen contribución" value={`${ev.contribution_margin_pct}%`} sub="(precio − var.) / precio" testid="prof-contrib" />
            <Kpi label="Margen EBITDA" value={`${ev.ebitda_margin_pct}%`} sub={`EBITDA ${money(ev.monthly.ebitda)}/mes`} testid="prof-ebitda" />
            <Kpi label="Índice rentab." value={ev.profitability_index ?? "—"} sub=">1 crea valor" testid="prof-pi" />
            <Kpi label="Margen seguridad" value={ev.safety_margin_pct == null ? "—" : `${ev.safety_margin_pct}%`} sub="ml plan sobre equilibrio" testid="prof-safety" />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">Flujo acumulado (mes 0 = inversión)</p>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={ev.cumulative}>
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={(x) => `${Math.round(x / 1000)}k`} width={48} />
                  <Tooltip formatter={(x) => money(x)} labelFormatter={(l) => `Mes ${l}`} />
                  <ReferenceLine y={0} stroke="#334155" />
                  <Bar dataKey="cumulative" name="Acumulado">
                    {ev.cumulative.map((c) => <Cell key={c.month} fill={c.cumulative >= 0 ? v.bar : "#94a3b8"} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">Sensibilidad del VAN (±10%)</p>
              <table className="w-full text-xs" data-testid="prof-sensitivity">
                <thead><tr className="text-[10px] uppercase text-slate-500 border-b"><th className="text-left py-1">Variable</th><th className="text-right">Escenario −</th><th className="text-right">Escenario +</th></tr></thead>
                <tbody>
                  {ev.sensitivity.map((s) => (
                    <tr key={s.var} className="border-b border-slate-100">
                      <td className="py-1">{s.var}</td>
                      <td className={`text-right font-mono ${s.minus < 0 ? "text-rose-600" : "text-emerald-700"}`}>{money(s.minus)}</td>
                      <td className={`text-right font-mono ${s.plus < 0 ? "text-rose-600" : "text-emerald-700"}`}>{money(s.plus)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-[10px] text-slate-500 mt-2">
                Mensual: ingreso {money(ev.monthly.revenue)} · var. {money(ev.monthly.variable)} · fijo {money(ev.monthly.fixed)} · ISR {money(ev.monthly.tax)} · FCF <b>{money(ev.monthly.fcf)}</b>
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
